import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { formatCurrencyText } from '../../../utils/Utils';
import {
    priceBandScale,
    priceBandMarkers,
    priceBandSummary,
    assignLabelRows,
    operatorTextOptions,
    formatDelivery,
} from './marketplaceProfile';

// Price band of the marketplace operator's product detail hero: a dot per marketplace
// minimum, a triangle for the operator's own (1P) price and a diamond for the buybox
// price, on one linear scale (the look follows the competitive profile's PriceRangeBand
// and its pux-range CSS). An own store (the operator's shop on another marketplace) is a
// hollow teal circle linking to its page; it is on the scale but is no competitor.
// Placement comes from the pure helpers in marketplaceProfile.js.

const LABEL_ROW_HEIGHT = 30;
const PRIORITY = { first_party: 2, buybox: 2, marketplace: 1, own_store: 0.5 };

const RED = '#dc2626';
const GREEN = '#16a34a';

// Width of the bar in px, kept current while the window or the pane resizes.
const useElementWidth = (ref, active) => {
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!active || !el) return undefined;
        const measure = () => setWidth(el.clientWidth || 0);
        measure();
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(measure);
            observer.observe(el);
            return () => observer.disconnect();
        }
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [ref, active]);
    return width;
};

const MarketplacePriceBand = ({ t, summary, config, settings, currency, onSellersClick }) => {
    const barRef = useRef(null);
    const operator = operatorTextOptions(settings);
    const labels = {
        first_party: t('product_detail.marketplace.first_party_badge', operator),
        buybox: t('product_detail.marketplace.band_buybox'),
    };
    const scale = useMemo(() => priceBandScale(summary, labels),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [summary, labels.first_party, labels.buybox]);
    const markers = useMemo(() => priceBandMarkers(summary, config, labels),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [summary, config, labels.first_party, labels.buybox]);
    const hasBand = !!scale && markers.length > 0;
    const width = useElementWidth(barRef, hasBand);

    if (!hasBand) return null;

    const rows = assignLabelRows(
        markers.map((marker) => ({ pct: marker.pct, priority: marker.counted ? PRIORITY[marker.type] : 0 })),
        width,
    );
    const usedRows = rows.reduce((max, row) => (row == null ? max : Math.max(max, row + 1)), 0);

    const marketplacePrices = markers.filter((m) => m.type === 'marketplace').map((m) => m.pct);
    const fillLeft = Math.min(...marketplacePrices);
    const fillRight = Math.max(...marketplacePrices);

    const price = (value) => formatCurrencyText(value, currency);
    const tooltipOf = (marker) => {
        if (marker.type === 'first_party') return `${marker.label} · ${price(marker.price)}`;
        if (marker.type === 'own_store') {
            return [
                marker.seller,
                t('product_detail.marketplace.band_own_store', { marketplace: marker.label }),
                price(marker.price),
                formatDelivery(marker.delivery, t),
            ].filter(Boolean).join(' · ');
        }
        if (marker.type === 'buybox') {
            return [marker.label, price(marker.price),
                marker.seller ? `${t('product_detail.marketplace.buybox_holder')}: ${marker.seller}` : null]
                .filter(Boolean).join(' · ');
        }
        return [
            marker.label,
            price(marker.price),
            t('product_detail.marketplace.n_sellers', { count: marker.count }),
            marker.seller ? t('product_detail.marketplace.band_cheapest', { seller: marker.seller }) : null,
            marker.counted ? null : t('product_detail.marketplace.band_extra'),
        ].filter(Boolean).join(' · ');
    };

    const markerClass = (marker) => `pux-mp-band-${marker.type === 'marketplace' ? 'dot' : marker.type}`
        + (marker.type === 'marketplace' && !marker.counted ? ' extra' : '')
        + (marker.cheaperThanBuybox ? ' cheaper' : '');

    const summaryInfo = priceBandSummary(summary);
    let summaryText = null;
    if (summaryInfo) {
        const subject = summaryInfo.subject === 'first_party'
            ? t('product_detail.marketplace.band_subject_first_party', operator)
            : t('product_detail.marketplace.band_subject_buybox');
        const parts = [];
        if (summaryInfo.direction) {
            parts.push(t(`product_detail.marketplace.band_${summaryInfo.direction}`,
                { subject, pct: summaryInfo.pct.toFixed(2) }));
        }
        if (summaryInfo.rank != null) {
            parts.push(t('product_detail.marketplace.band_rank', { rank: summaryInfo.rank, total: summaryInfo.total }));
        }
        const color = summaryInfo.direction === 'more' ? RED : (summaryInfo.direction === 'less' ? GREEN : undefined);
        summaryText = <div className="pux-mp-band-summary" style={color ? { color } : undefined}>{parts.join(' · ')}</div>;
    }

    return (
        <div className="pux-mp-band" role="group" aria-label={t('product_detail.marketplace.band_aria')}>
            <div className="pux-mp-band-labels" style={{ height: usedRows * LABEL_ROW_HEIGHT }}>
                {markers.map((marker, index) => {
                    const row = rows[index];
                    if (row == null) return null;
                    return (
                        <span key={`${marker.type}-${marker.key}`}
                              className={`pux-mp-band-label ${markerClass(marker)}`}
                              style={{ left: marker.pct + '%', top: (usedRows - 1 - row) * LABEL_ROW_HEIGHT }}>
                            <span className="pux-mp-band-label-name">{marker.label}</span>
                            <span className="pux-mp-band-label-price">{price(marker.price)}</span>
                        </span>
                    );
                })}
            </div>

            <div className="pux-range pux-mp-band-bar" ref={barRef}>
                {marketplacePrices.length > 1 &&
                    <div className="pux-range-fill" style={{ left: fillLeft + '%', right: (100 - fillRight) + '%' }} />}
                {markers.map((marker) => {
                    const style = { left: marker.pct + '%' };
                    const tooltip = tooltipOf(marker);
                    if (marker.type === 'first_party') {
                        return <span key="first_party" className={`pux-mp-band-mark ${markerClass(marker)}`} style={style}
                                     title={tooltip} aria-label={tooltip}>▲</span>;
                    }
                    if (marker.type === 'buybox') {
                        return <span key="buybox" className={`pux-mp-band-mark ${markerClass(marker)}`} style={style}
                                     title={tooltip} aria-label={tooltip}>◆</span>;
                    }
                    if (marker.type === 'own_store') {
                        return marker.url
                            ? <a key={`own_store-${marker.key}`} href={marker.url} target="_blank" rel="noopener noreferrer"
                                 className={`pux-mp-band-mark ${markerClass(marker)} clickable`} style={style}
                                 title={tooltip} aria-label={tooltip} />
                            : <span key={`own_store-${marker.key}`} className={`pux-mp-band-mark ${markerClass(marker)}`} style={style}
                                    title={tooltip} aria-label={tooltip} />;
                    }
                    if (onSellersClick && marker.count > 0) {
                        return <button type="button" key={marker.key} className={`pux-mp-band-mark ${markerClass(marker)} clickable`}
                                       style={style} title={tooltip} aria-label={tooltip}
                                       onClick={() => onSellersClick(marker.key)} />;
                    }
                    return <span key={marker.key} className={`pux-mp-band-mark ${markerClass(marker)}`} style={style}
                                 title={tooltip} aria-label={tooltip} />;
                })}
            </div>

            <div className="pux-mp-band-ends">
                <span className="pux-mp-band-end" title={`${scale.minLabel} · ${price(scale.min)}`}>
                    {scale.minLabel} <b>{price(scale.min)}</b>
                </span>
                <span className="pux-mp-band-end right" title={`${scale.maxLabel} · ${price(scale.max)}`}>
                    {scale.maxLabel} <b>{price(scale.max)}</b>
                </span>
            </div>

            {summaryText}
        </div>
    );
};

export default MarketplacePriceBand;
